import type {GeoObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {RootManagerNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ReturnedNodeDataDict} from '../../ExampleUtils';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {MaterialsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import {MeshStandardBuilderMatNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshStandardBuilder';
import {ParamType} from '../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';
import {CopNetworkObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/CopNetwork';
import {ActorSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import {CameraControlsSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_ground(parentNode: RootManagerNode) {
		var ground = parentNode.createNode('geo');
		ground.setName('ground');
		const ground_nodes: ReturnedNodeDataDict = {};
		function create_material1(ground: GeoObjNode) {
			var material1 = ground.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(0, 300);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandardBuilder1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(ground: GeoObjNode) {
			var MAT = ground.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandardBuilder1(MAT: MaterialsNetworkSopNode) {
				var meshStandardBuilder1 = MAT.createNode('meshStandardBuilder');
				meshStandardBuilder1.setName('meshStandardBuilder1');
				const meshStandardBuilder1_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var globals1 = meshStandardBuilder1.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-450, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var output1 = meshStandardBuilder1.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(400, 0);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_vec3ToFloat1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var vec3ToFloat1 = meshStandardBuilder1.createNode('vec3ToFloat');
					vec3ToFloat1.setName('vec3ToFloat1');
					const vec3ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat1.uiData.setPosition(-200, 200);
					vec3ToFloat1.params.postCreateSpareParams();
					vec3ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat1, children: vec3ToFloat1_nodes};
				}
				function create_floatToVec2_1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var floatToVec2_1 = meshStandardBuilder1.createNode('floatToVec2');
					floatToVec2_1.setName('floatToVec2_1');
					const floatToVec2_1_nodes: ReturnedNodeDataDict = {};
					floatToVec2_1.uiData.setPosition(-100, 200);
					floatToVec2_1.params.postCreateSpareParams();
					floatToVec2_1.params.runOnSceneLoadHooks();
					return {node: floatToVec2_1, children: floatToVec2_1_nodes};
				}
				function create_checkers1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var checkers1 = meshStandardBuilder1.createNode('checkers');
					checkers1.setName('checkers1');
					const checkers1_nodes: ReturnedNodeDataDict = {};
					checkers1.uiData.setPosition(50, 200);
					checkers1.params.postCreateSpareParams();
					checkers1.params.runOnSceneLoadHooks();
					return {node: checkers1, children: checkers1_nodes};
				}
				function create_mix1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var mix1 = meshStandardBuilder1.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(250, 0);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {spare: true, editable: false});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant1 = meshStandardBuilder1.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(50, -150);
					constant1.p.type.set(4);
					constant1.p.color.set([0.24313725490196078, 0.5098039215686274, 0.8549019607843137]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant2 = meshStandardBuilder1.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(0, 0);
					constant2.p.type.set(4);
					constant2.p.color.set([0.047058823529411764, 0.10196078431372549, 0.17647058823529413]);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				meshStandardBuilder1_nodes['globals1'] = create_globals1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'] = create_output1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['vec3ToFloat1'] = create_vec3ToFloat1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['floatToVec2_1'] = create_floatToVec2_1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['checkers1'] = create_checkers1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['mix1'] = create_mix1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant1'] = create_constant1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant2'] = create_constant2(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'].node.setInput(
					'color',
					meshStandardBuilder1_nodes['mix1'].node,
					'mix'
				);
				meshStandardBuilder1_nodes['vec3ToFloat1'].node.setInput(
					'vec',
					meshStandardBuilder1_nodes['globals1'].node,
					'position'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'x',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'x'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'y',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'z'
				);
				meshStandardBuilder1_nodes['checkers1'].node.setInput(
					'uv',
					meshStandardBuilder1_nodes['floatToVec2_1'].node,
					'vec2'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value0',
					meshStandardBuilder1_nodes['constant1'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value1',
					meshStandardBuilder1_nodes['constant2'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'blend',
					meshStandardBuilder1_nodes['checkers1'].node,
					'checker'
				);
				if (meshStandardBuilder1.childrenController) {
					meshStandardBuilder1.childrenController.selection.set([]);
				}
				meshStandardBuilder1.uiData.setPosition(0, 200);
				meshStandardBuilder1.params.postCreateSpareParams();
				meshStandardBuilder1.params.runOnSceneLoadHooks();
				return {node: meshStandardBuilder1, children: meshStandardBuilder1_nodes};
			}
			MAT_nodes['meshStandardBuilder1'] = create_meshStandardBuilder1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-200, 300);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_box1(ground: GeoObjNode) {
			var box1 = ground.createNode('box');
			box1.setName('box1');
			const box1_nodes: ReturnedNodeDataDict = {};
			box1.uiData.setPosition(0, 100);
			box1.p.sizes.set([9.98, 1, 9.98]);
			box1.p.center.set([0, -0.5, 0]);
			box1.params.postCreateSpareParams();
			box1.params.runOnSceneLoadHooks();
			return {node: box1, children: box1_nodes};
		}
		ground_nodes['material1'] = create_material1(ground);
		ground_nodes['MAT'] = create_MAT(ground);
		ground_nodes['box1'] = create_box1(ground);
		ground_nodes['material1'].node.setInput(0, ground_nodes['box1'].node);
		if (ground.childrenController) {
			ground.childrenController.selection.set([]);
		}
		ground.uiData.setPosition(-50, -450);
		ground.flags.display.set(true);
		ground.params.postCreateSpareParams();
		ground.params.runOnSceneLoadHooks();
		return {node: ground, children: ground_nodes};
	}
	function create_COP(parentNode: RootManagerNode) {
		var COP = parentNode.createNode('copNetwork');
		COP.setName('COP');
		const COP_nodes: ReturnedNodeDataDict = {};
		function create_envMap(COP: CopNetworkObjNode) {
			var envMap = COP.createNode('envMap');
			envMap.setName('envMap');
			const envMap_nodes: ReturnedNodeDataDict = {};
			envMap.uiData.setPosition(50, 250);
			envMap.params.postCreateSpareParams();
			envMap.params.runOnSceneLoadHooks();
			return {node: envMap, children: envMap_nodes};
		}
		function create_imageEnv(COP: CopNetworkObjNode) {
			var imageEnv = COP.createNode('imageEXR');
			imageEnv.setName('imageEnv');
			const imageEnv_nodes: ReturnedNodeDataDict = {};
			imageEnv.uiData.setPosition(50, 100);
			imageEnv.p.tminFilter.set(true);
			imageEnv.p.tmagFilter.set(true);
			imageEnv.p.tanisotropy.set(true);
			imageEnv.p.useRendererMaxAnisotropy.set(true);
			imageEnv.params.postCreateSpareParams();
			imageEnv.params.runOnSceneLoadHooks();
			return {node: imageEnv, children: imageEnv_nodes};
		}
		function create_image1(COP: CopNetworkObjNode) {
			var image1 = COP.createNode('image');
			image1.setName('image1');
			const image1_nodes: ReturnedNodeDataDict = {};
			image1.uiData.setPosition(-200, 100);
			image1.params.postCreateSpareParams();
			image1.params.runOnSceneLoadHooks();
			return {node: image1, children: image1_nodes};
		}
		COP_nodes['envMap'] = create_envMap(COP);
		COP_nodes['imageEnv'] = create_imageEnv(COP);
		COP_nodes['image1'] = create_image1(COP);
		COP_nodes['envMap'].node.setInput(0, COP_nodes['imageEnv'].node);
		if (COP.childrenController) {
			COP.childrenController.selection.set([COP_nodes['image1'].node]);
		}
		COP.uiData.setPosition(-300, -50);
		COP.params.postCreateSpareParams();
		COP.params.runOnSceneLoadHooks();
		return {node: COP, children: COP_nodes};
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(-50, 0);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, 0);
			spotLight1.p.decay.set(0.1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.latitude.set(25.2);
			polarTransform1.p.depth.set(3);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 600);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		function create_objectProperties1(lights: GeoObjNode) {
			var objectProperties1 = lights.createNode('objectProperties');
			objectProperties1.setName('objectProperties1');
			const objectProperties1_nodes: ReturnedNodeDataDict = {};
			objectProperties1.uiData.setPosition(300, 300);
			objectProperties1.p.tname.set(true);
			objectProperties1.p.name.set('spotLightToToggle');
			objectProperties1.params.postCreateSpareParams();
			objectProperties1.params.runOnSceneLoadHooks();
			return {node: objectProperties1, children: objectProperties1_nodes};
		}
		function create_actor_toggle(lights: GeoObjNode) {
			var actor_toggle = lights.createNode('actor');
			actor_toggle.setName('actor_toggle');
			const actor_toggle_nodes: ReturnedNodeDataDict = {};
			function create_constant3(actor_toggle: ActorSopNode) {
				var constant3 = actor_toggle.createNode('constant');
				constant3.setName('constant3');
				const constant3_nodes: ReturnedNodeDataDict = {};
				constant3.uiData.setPosition(-250, 250);
				constant3.p.type.set(4);
				constant3.p.string.set('on');
				constant3.params.postCreateSpareParams();
				constant3.params.runOnSceneLoadHooks();
				return {node: constant3, children: constant3_nodes};
			}
			function create_setSpotLightIntensity1(actor_toggle: ActorSopNode) {
				var setSpotLightIntensity1 = actor_toggle.createNode('setSpotLightIntensity');
				setSpotLightIntensity1.setName('setSpotLightIntensity1');
				const setSpotLightIntensity1_nodes: ReturnedNodeDataDict = {};
				setSpotLightIntensity1.uiData.setPosition(350, 150);
				setSpotLightIntensity1.params.postCreateSpareParams();
				setSpotLightIntensity1.params.runOnSceneLoadHooks();
				return {node: setSpotLightIntensity1, children: setSpotLightIntensity1_nodes};
			}
			function create_onObjectAttributeUpdate1(actor_toggle: ActorSopNode) {
				var onObjectAttributeUpdate1 = actor_toggle.createNode('onObjectAttributeUpdate');
				onObjectAttributeUpdate1.setName('onObjectAttributeUpdate1');
				const onObjectAttributeUpdate1_nodes: ReturnedNodeDataDict = {};
				onObjectAttributeUpdate1.uiData.setPosition(-50, 150);
				onObjectAttributeUpdate1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				onObjectAttributeUpdate1.params.postCreateSpareParams();
				onObjectAttributeUpdate1.params.runOnSceneLoadHooks();
				return {node: onObjectAttributeUpdate1, children: onObjectAttributeUpdate1_nodes};
			}
			actor_toggle_nodes['constant3'] = create_constant3(actor_toggle);
			actor_toggle_nodes['setSpotLightIntensity1'] = create_setSpotLightIntensity1(actor_toggle);
			actor_toggle_nodes['onObjectAttributeUpdate1'] = create_onObjectAttributeUpdate1(actor_toggle);
			actor_toggle_nodes['setSpotLightIntensity1'].node.setInput(
				'trigger',
				actor_toggle_nodes['onObjectAttributeUpdate1'].node,
				'trigger'
			);
			actor_toggle_nodes['setSpotLightIntensity1'].node.setInput(
				'intensity',
				actor_toggle_nodes['onObjectAttributeUpdate1'].node,
				'newValue'
			);
			actor_toggle_nodes['onObjectAttributeUpdate1'].node.setInput(
				'attribName',
				actor_toggle_nodes['constant3'].node,
				'val'
			);
			if (actor_toggle.childrenController) {
				actor_toggle.childrenController.selection.set([]);
			}
			actor_toggle.uiData.setPosition(300, 450);
			actor_toggle.params.postCreateSpareParams();
			actor_toggle.params.runOnSceneLoadHooks();
			return {node: actor_toggle, children: actor_toggle_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['objectProperties1'] = create_objectProperties1(lights);
		lights_nodes['actor_toggle'] = create_actor_toggle(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['actor_toggle'].node);
		lights_nodes['objectProperties1'].node.setInput(0, lights_nodes['polarTransform1'].node);
		lights_nodes['actor_toggle'].node.setInput(0, lights_nodes['objectProperties1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([]);
		}
		lights.uiData.setPosition(-50, -250);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_perspectiveCamera1(cameras: GeoObjNode) {
			var perspectiveCamera1 = cameras.createNode('perspectiveCamera');
			perspectiveCamera1.setName('perspectiveCamera1');
			const perspectiveCamera1_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera1.uiData.setPosition(0, -50);
			perspectiveCamera1.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera1.params.postCreateSpareParams();
			perspectiveCamera1.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera1, children: perspectiveCamera1_nodes};
		}
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([0.6095631336248215, 0.3492780168039058, 0.3818852299846185]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.flags.display.set(true);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		cameras_nodes['perspectiveCamera1'] = create_perspectiveCamera1(cameras);
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([]);
		}
		cameras.uiData.setPosition(-50, -150);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	function create_buttons(parentNode: RootManagerNode) {
		var buttons = parentNode.createNode('geo');
		buttons.setName('buttons');
		const buttons_nodes: ReturnedNodeDataDict = {};
		function create_sphere1(buttons: GeoObjNode) {
			var sphere1 = buttons.createNode('sphere');
			sphere1.setName('sphere1');
			const sphere1_nodes: ReturnedNodeDataDict = {};
			sphere1.uiData.setPosition(0, 50);
			sphere1.params.postCreateSpareParams();
			sphere1.params.runOnSceneLoadHooks();
			return {node: sphere1, children: sphere1_nodes};
		}
		function create_transform1(buttons: GeoObjNode) {
			var transform1 = buttons.createNode('transform');
			transform1.setName('transform1');
			const transform1_nodes: ReturnedNodeDataDict = {};
			transform1.uiData.setPosition(0, 200);
			transform1.p.t.set([0, 1, 0]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return {node: transform1, children: transform1_nodes};
		}
		function create_material1(buttons: GeoObjNode) {
			var material1 = buttons.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(0, 350);
			material1.p.material.set('../MAT/meshStandard1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(buttons: GeoObjNode) {
			var MAT = buttons.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandard1(MAT: MaterialsNetworkSopNode) {
				var meshStandard1 = MAT.createNode('meshStandard');
				meshStandard1.setName('meshStandard1');
				const meshStandard1_nodes: ReturnedNodeDataDict = {};
				meshStandard1.uiData.setPosition(0, -50);
				meshStandard1.p.color.set([0.5, 0.5, 0.5]);
				meshStandard1.p.useMap.set(true);
				meshStandard1.p.map.set('../../../COP/image1');
				meshStandard1.p.useEnvMap.set(true);
				meshStandard1.p.envMap.set('../../../COP/envMap');
				meshStandard1.p.metalness.set(1);
				meshStandard1.p.roughness.set(0);
				meshStandard1.params.postCreateSpareParams();
				meshStandard1.params.runOnSceneLoadHooks();
				return {node: meshStandard1, children: meshStandard1_nodes};
			}
			function create_meshStandard2(MAT: MaterialsNetworkSopNode) {
				var meshStandard2 = MAT.createNode('meshStandard');
				meshStandard2.setName('meshStandard2');
				const meshStandard2_nodes: ReturnedNodeDataDict = {};
				meshStandard2.uiData.setPosition(0, 150);
				meshStandard2.p.color.set([0.5, 0.5, 0.5]);
				meshStandard2.p.useVertexColors.set(true);
				meshStandard2.p.map.set('../../../COP/image1');
				meshStandard2.p.useEnvMap.set(true);
				meshStandard2.p.envMap.set('../../../COP/envMap');
				meshStandard2.p.roughness.set(0.21);
				meshStandard2.params.postCreateSpareParams();
				meshStandard2.params.runOnSceneLoadHooks();
				return {node: meshStandard2, children: meshStandard2_nodes};
			}
			MAT_nodes['meshStandard1'] = create_meshStandard1(MAT);
			MAT_nodes['meshStandard2'] = create_meshStandard2(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-200, 350);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_roundedBox1(buttons: GeoObjNode) {
			var roundedBox1 = buttons.createNode('roundedBox');
			roundedBox1.setName('roundedBox1');
			const roundedBox1_nodes: ReturnedNodeDataDict = {};
			roundedBox1.uiData.setPosition(200, -200);
			roundedBox1.p.sizes.set([2, 0.6, 1]);
			roundedBox1.p.divisions.set(7);
			roundedBox1.params.postCreateSpareParams();
			roundedBox1.params.runOnSceneLoadHooks();
			return {node: roundedBox1, children: roundedBox1_nodes};
		}
		function create_transform2(buttons: GeoObjNode) {
			var transform2 = buttons.createNode('transform');
			transform2.setName('transform2');
			const transform2_nodes: ReturnedNodeDataDict = {};
			transform2.uiData.setPosition(350, 200);
			transform2.p.t.set([2.14732766532833, 0.0985634123757465, 0]);
			transform2.params.postCreateSpareParams();
			transform2.params.runOnSceneLoadHooks();
			return {node: transform2, children: transform2_nodes};
		}
		function create_merge1(buttons: GeoObjNode) {
			var merge1 = buttons.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(150, 650);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		function create_actor_on_hover_set_cursor_to_pointer(buttons: GeoObjNode) {
			var actor_on_hover_set_cursor_to_pointer = buttons.createNode('actor');
			actor_on_hover_set_cursor_to_pointer.setName('actor_on_hover_set_cursor_to_pointer');
			const actor_on_hover_set_cursor_to_pointer_nodes: ReturnedNodeDataDict = {};
			function create_onObjectHover1(actor_on_hover_set_cursor_to_pointer: ActorSopNode) {
				var onObjectHover1 = actor_on_hover_set_cursor_to_pointer.createNode('onObjectHover');
				onObjectHover1.setName('onObjectHover1');
				const onObjectHover1_nodes: ReturnedNodeDataDict = {};
				onObjectHover1.uiData.setPosition(100, -200);
				onObjectHover1.params.postCreateSpareParams();
				onObjectHover1.params.runOnSceneLoadHooks();
				return {node: onObjectHover1, children: onObjectHover1_nodes};
			}
			function create_setViewer1(actor_on_hover_set_cursor_to_pointer: ActorSopNode) {
				var setViewer1 = actor_on_hover_set_cursor_to_pointer.createNode('setViewer');
				setViewer1.setName('setViewer1');
				const setViewer1_nodes: ReturnedNodeDataDict = {};
				setViewer1.uiData.setPosition(400, -200);
				setViewer1.params.postCreateSpareParams();
				setViewer1.params.runOnSceneLoadHooks();
				return {node: setViewer1, children: setViewer1_nodes};
			}
			actor_on_hover_set_cursor_to_pointer_nodes['onObjectHover1'] = create_onObjectHover1(
				actor_on_hover_set_cursor_to_pointer
			);
			actor_on_hover_set_cursor_to_pointer_nodes['setViewer1'] = create_setViewer1(
				actor_on_hover_set_cursor_to_pointer
			);
			actor_on_hover_set_cursor_to_pointer_nodes['setViewer1'].node.setInput(
				'trigger',
				actor_on_hover_set_cursor_to_pointer_nodes['onObjectHover1'].node,
				'trigger'
			);
			actor_on_hover_set_cursor_to_pointer_nodes['setViewer1'].node.setInput(
				'set',
				actor_on_hover_set_cursor_to_pointer_nodes['onObjectHover1'].node,
				'hovered'
			);
			if (actor_on_hover_set_cursor_to_pointer.childrenController) {
				actor_on_hover_set_cursor_to_pointer.childrenController.selection.set([]);
			}
			actor_on_hover_set_cursor_to_pointer.uiData.setPosition(150, 800);
			actor_on_hover_set_cursor_to_pointer.params.postCreateSpareParams();
			actor_on_hover_set_cursor_to_pointer.params.runOnSceneLoadHooks();
			return {node: actor_on_hover_set_cursor_to_pointer, children: actor_on_hover_set_cursor_to_pointer_nodes};
		}
		function create_actor_on_hover_update_material(buttons: GeoObjNode) {
			var actor_on_hover_update_material = buttons.createNode('actor');
			actor_on_hover_update_material.setName('actor_on_hover_update_material');
			const actor_on_hover_update_material_nodes: ReturnedNodeDataDict = {};
			function create_setMaterialColor1(actor_on_hover_update_material: ActorSopNode) {
				var setMaterialColor1 = actor_on_hover_update_material.createNode('setMaterialColor');
				setMaterialColor1.setName('setMaterialColor1');
				const setMaterialColor1_nodes: ReturnedNodeDataDict = {};
				setMaterialColor1.uiData.setPosition(400, -50);
				setMaterialColor1.params.postCreateSpareParams();
				setMaterialColor1.params.runOnSceneLoadHooks();
				return {node: setMaterialColor1, children: setMaterialColor1_nodes};
			}
			function create_onObjectHover1(actor_on_hover_update_material: ActorSopNode) {
				var onObjectHover1 = actor_on_hover_update_material.createNode('onObjectHover');
				onObjectHover1.setName('onObjectHover1');
				const onObjectHover1_nodes: ReturnedNodeDataDict = {};
				onObjectHover1.uiData.setPosition(-100, -50);
				onObjectHover1.params.postCreateSpareParams();
				onObjectHover1.params.runOnSceneLoadHooks();
				return {node: onObjectHover1, children: onObjectHover1_nodes};
			}
			function create_twoWaySwitch1(actor_on_hover_update_material: ActorSopNode) {
				var twoWaySwitch1 = actor_on_hover_update_material.createNode('twoWaySwitch');
				twoWaySwitch1.setName('twoWaySwitch1');
				const twoWaySwitch1_nodes: ReturnedNodeDataDict = {};
				twoWaySwitch1.uiData.setPosition(200, 50);
				twoWaySwitch1.addParam(ParamType.BOOLEAN, 'condition', false, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				twoWaySwitch1.addParam(ParamType.COLOR, 'ifTrue', [1, 1, 1], {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				twoWaySwitch1.params.get('ifTrue')!.set([0, 0, 0]);
				twoWaySwitch1.addParam(ParamType.COLOR, 'ifFalse', [1, 1, 1], {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				twoWaySwitch1.params.get('ifFalse')!.set([0, 0, 0]);
				twoWaySwitch1.params.postCreateSpareParams();
				twoWaySwitch1.params.runOnSceneLoadHooks();
				return {node: twoWaySwitch1, children: twoWaySwitch1_nodes};
			}
			function create_constant1(actor_on_hover_update_material: ActorSopNode) {
				var constant1 = actor_on_hover_update_material.createNode('constant');
				constant1.setName('constant1');
				const constant1_nodes: ReturnedNodeDataDict = {};
				constant1.uiData.setPosition(-100, 100);
				constant1.p.type.set(1);
				constant1.p.color.set([1, 1, 1]);
				constant1.params.postCreateSpareParams();
				constant1.params.runOnSceneLoadHooks();
				return {node: constant1, children: constant1_nodes};
			}
			function create_constant2(actor_on_hover_update_material: ActorSopNode) {
				var constant2 = actor_on_hover_update_material.createNode('constant');
				constant2.setName('constant2');
				const constant2_nodes: ReturnedNodeDataDict = {};
				constant2.uiData.setPosition(-100, 200);
				constant2.p.type.set(1);
				constant2.p.color.set([0.5, 0.5, 0.5]);
				constant2.params.postCreateSpareParams();
				constant2.params.runOnSceneLoadHooks();
				return {node: constant2, children: constant2_nodes};
			}
			actor_on_hover_update_material_nodes['setMaterialColor1'] =
				create_setMaterialColor1(actor_on_hover_update_material);
			actor_on_hover_update_material_nodes['onObjectHover1'] =
				create_onObjectHover1(actor_on_hover_update_material);
			actor_on_hover_update_material_nodes['twoWaySwitch1'] =
				create_twoWaySwitch1(actor_on_hover_update_material);
			actor_on_hover_update_material_nodes['constant1'] = create_constant1(actor_on_hover_update_material);
			actor_on_hover_update_material_nodes['constant2'] = create_constant2(actor_on_hover_update_material);
			actor_on_hover_update_material_nodes['setMaterialColor1'].node.setInput(
				'trigger',
				actor_on_hover_update_material_nodes['onObjectHover1'].node,
				'trigger'
			);
			actor_on_hover_update_material_nodes['setMaterialColor1'].node.setInput(
				'color',
				actor_on_hover_update_material_nodes['twoWaySwitch1'].node,
				'val'
			);
			actor_on_hover_update_material_nodes['twoWaySwitch1'].node.setInput(
				'condition',
				actor_on_hover_update_material_nodes['onObjectHover1'].node,
				'hovered'
			);
			actor_on_hover_update_material_nodes['twoWaySwitch1'].node.setInput(
				'ifTrue',
				actor_on_hover_update_material_nodes['constant1'].node,
				'val'
			);
			actor_on_hover_update_material_nodes['twoWaySwitch1'].node.setInput(
				'ifFalse',
				actor_on_hover_update_material_nodes['constant2'].node,
				'val'
			);
			if (actor_on_hover_update_material.childrenController) {
				actor_on_hover_update_material.childrenController.selection.set([
					actor_on_hover_update_material_nodes['setMaterialColor1'].node,
					actor_on_hover_update_material_nodes['onObjectHover1'].node,
					actor_on_hover_update_material_nodes['twoWaySwitch1'].node,
					actor_on_hover_update_material_nodes['constant1'].node,
					actor_on_hover_update_material_nodes['constant2'].node,
				]);
			}
			actor_on_hover_update_material.uiData.setPosition(0, 500);
			actor_on_hover_update_material.params.postCreateSpareParams();
			actor_on_hover_update_material.params.runOnSceneLoadHooks();
			return {node: actor_on_hover_update_material, children: actor_on_hover_update_material_nodes};
		}
		function create_actor_on_hover_update_material1(buttons: GeoObjNode) {
			var actor_on_hover_update_material1 = buttons.createNode('actor');
			actor_on_hover_update_material1.setName('actor_on_hover_update_material1');
			const actor_on_hover_update_material1_nodes: ReturnedNodeDataDict = {};
			function create_setMaterialColor1(actor_on_hover_update_material1: ActorSopNode) {
				var setMaterialColor1 = actor_on_hover_update_material1.createNode('setMaterialColor');
				setMaterialColor1.setName('setMaterialColor1');
				const setMaterialColor1_nodes: ReturnedNodeDataDict = {};
				setMaterialColor1.uiData.setPosition(350, 250);
				setMaterialColor1.params.postCreateSpareParams();
				setMaterialColor1.params.runOnSceneLoadHooks();
				return {node: setMaterialColor1, children: setMaterialColor1_nodes};
			}
			function create_onObjectHover1(actor_on_hover_update_material1: ActorSopNode) {
				var onObjectHover1 = actor_on_hover_update_material1.createNode('onObjectHover');
				onObjectHover1.setName('onObjectHover1');
				const onObjectHover1_nodes: ReturnedNodeDataDict = {};
				onObjectHover1.uiData.setPosition(-150, 250);
				onObjectHover1.params.postCreateSpareParams();
				onObjectHover1.params.runOnSceneLoadHooks();
				return {node: onObjectHover1, children: onObjectHover1_nodes};
			}
			function create_twoWaySwitch1(actor_on_hover_update_material1: ActorSopNode) {
				var twoWaySwitch1 = actor_on_hover_update_material1.createNode('twoWaySwitch');
				twoWaySwitch1.setName('twoWaySwitch1');
				const twoWaySwitch1_nodes: ReturnedNodeDataDict = {};
				twoWaySwitch1.uiData.setPosition(150, 350);
				twoWaySwitch1.addParam(ParamType.BOOLEAN, 'condition', false, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				twoWaySwitch1.addParam(ParamType.COLOR, 'ifTrue', [0, 0, 0], {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				twoWaySwitch1.addParam(ParamType.COLOR, 'ifFalse', [0, 0, 0], {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				twoWaySwitch1.params.postCreateSpareParams();
				twoWaySwitch1.params.runOnSceneLoadHooks();
				return {node: twoWaySwitch1, children: twoWaySwitch1_nodes};
			}
			function create_constant1(actor_on_hover_update_material1: ActorSopNode) {
				var constant1 = actor_on_hover_update_material1.createNode('constant');
				constant1.setName('constant1');
				const constant1_nodes: ReturnedNodeDataDict = {};
				constant1.uiData.setPosition(-150, 400);
				constant1.p.type.set(1);
				constant1.p.color.set([1, 1, 1]);
				constant1.params.postCreateSpareParams();
				constant1.params.runOnSceneLoadHooks();
				return {node: constant1, children: constant1_nodes};
			}
			function create_constant2(actor_on_hover_update_material1: ActorSopNode) {
				var constant2 = actor_on_hover_update_material1.createNode('constant');
				constant2.setName('constant2');
				const constant2_nodes: ReturnedNodeDataDict = {};
				constant2.uiData.setPosition(-150, 500);
				constant2.p.type.set(1);
				constant2.p.color.set([0.5, 0.5, 0.5]);
				constant2.params.postCreateSpareParams();
				constant2.params.runOnSceneLoadHooks();
				return {node: constant2, children: constant2_nodes};
			}
			actor_on_hover_update_material1_nodes['setMaterialColor1'] = create_setMaterialColor1(
				actor_on_hover_update_material1
			);
			actor_on_hover_update_material1_nodes['onObjectHover1'] = create_onObjectHover1(
				actor_on_hover_update_material1
			);
			actor_on_hover_update_material1_nodes['twoWaySwitch1'] = create_twoWaySwitch1(
				actor_on_hover_update_material1
			);
			actor_on_hover_update_material1_nodes['constant1'] = create_constant1(actor_on_hover_update_material1);
			actor_on_hover_update_material1_nodes['constant2'] = create_constant2(actor_on_hover_update_material1);
			actor_on_hover_update_material1_nodes['setMaterialColor1'].node.setInput(
				'trigger',
				actor_on_hover_update_material1_nodes['onObjectHover1'].node,
				'trigger'
			);
			actor_on_hover_update_material1_nodes['setMaterialColor1'].node.setInput(
				'color',
				actor_on_hover_update_material1_nodes['twoWaySwitch1'].node,
				'val'
			);
			actor_on_hover_update_material1_nodes['twoWaySwitch1'].node.setInput(
				'condition',
				actor_on_hover_update_material1_nodes['onObjectHover1'].node,
				'hovered'
			);
			actor_on_hover_update_material1_nodes['twoWaySwitch1'].node.setInput(
				'ifTrue',
				actor_on_hover_update_material1_nodes['constant1'].node,
				'val'
			);
			actor_on_hover_update_material1_nodes['twoWaySwitch1'].node.setInput(
				'ifFalse',
				actor_on_hover_update_material1_nodes['constant2'].node,
				'val'
			);
			if (actor_on_hover_update_material1.childrenController) {
				actor_on_hover_update_material1.childrenController.selection.set([
					actor_on_hover_update_material1_nodes['setMaterialColor1'].node,
					actor_on_hover_update_material1_nodes['onObjectHover1'].node,
					actor_on_hover_update_material1_nodes['twoWaySwitch1'].node,
					actor_on_hover_update_material1_nodes['constant1'].node,
					actor_on_hover_update_material1_nodes['constant2'].node,
				]);
			}
			actor_on_hover_update_material1.uiData.setPosition(350, 500);
			actor_on_hover_update_material1.params.postCreateSpareParams();
			actor_on_hover_update_material1.params.runOnSceneLoadHooks();
			return {node: actor_on_hover_update_material1, children: actor_on_hover_update_material1_nodes};
		}
		function create_material2(buttons: GeoObjNode) {
			var material2 = buttons.createNode('material');
			material2.setName('material2');
			const material2_nodes: ReturnedNodeDataDict = {};
			material2.uiData.setPosition(350, 350);
			material2.p.material.set('../MAT/meshStandard2');
			material2.params.postCreateSpareParams();
			material2.params.runOnSceneLoadHooks();
			return {node: material2, children: material2_nodes};
		}
		function create_text1(buttons: GeoObjNode) {
			var text1 = buttons.createNode('text');
			text1.setName('text1');
			const text1_nodes: ReturnedNodeDataDict = {};
			text1.uiData.setPosition(450, -250);
			text1.p.text.set('click');
			text1.p.size.set(0.62);
			text1.p.segments.set(4);
			text1.p.justifyMode.set(2);
			text1.params.postCreateSpareParams();
			text1.params.runOnSceneLoadHooks();
			return {node: text1, children: text1_nodes};
		}
		function create_merge2(buttons: GeoObjNode) {
			var merge2 = buttons.createNode('merge');
			merge2.setName('merge2');
			const merge2_nodes: ReturnedNodeDataDict = {};
			merge2.uiData.setPosition(350, 50);
			merge2.p.compact.set(true);
			merge2.params.postCreateSpareParams();
			merge2.params.runOnSceneLoadHooks();
			return {node: merge2, children: merge2_nodes};
		}
		function create_transform3(buttons: GeoObjNode) {
			var transform3 = buttons.createNode('transform');
			transform3.setName('transform3');
			const transform3_nodes: ReturnedNodeDataDict = {};
			transform3.uiData.setPosition(450, -150);
			transform3.p.t.set([0, 0.24588851617607205, 0.27118340436319865]);
			transform3.p.r.set([-90, 0, 0]);
			transform3.params.postCreateSpareParams();
			transform3.params.runOnSceneLoadHooks();
			return {node: transform3, children: transform3_nodes};
		}
		function create_color1(buttons: GeoObjNode) {
			var color1 = buttons.createNode('color');
			color1.setName('color1');
			const color1_nodes: ReturnedNodeDataDict = {};
			color1.uiData.setPosition(450, -50);
			color1.p.color.set([0, 0, 0]);
			color1.params.postCreateSpareParams();
			color1.params.runOnSceneLoadHooks();
			return {node: color1, children: color1_nodes};
		}
		function create_color2(buttons: GeoObjNode) {
			var color2 = buttons.createNode('color');
			color2.setName('color2');
			const color2_nodes: ReturnedNodeDataDict = {};
			color2.uiData.setPosition(200, -50);
			color2.params.postCreateSpareParams();
			color2.params.runOnSceneLoadHooks();
			return {node: color2, children: color2_nodes};
		}
		function create_actor_move_down_on_click_and_update_light(buttons: GeoObjNode) {
			var actor_move_down_on_click_and_update_light = buttons.createNode('actor');
			actor_move_down_on_click_and_update_light.setName('actor_move_down_on_click_and_update_light');
			const actor_move_down_on_click_and_update_light_nodes: ReturnedNodeDataDict = {};
			function create_setObjectPosition1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var setObjectPosition1 = actor_move_down_on_click_and_update_light.createNode('setObjectPosition');
				setObjectPosition1.setName('setObjectPosition1');
				const setObjectPosition1_nodes: ReturnedNodeDataDict = {};
				setObjectPosition1.uiData.setPosition(400, 0);
				setObjectPosition1.params.postCreateSpareParams();
				setObjectPosition1.params.runOnSceneLoadHooks();
				return {node: setObjectPosition1, children: setObjectPosition1_nodes};
			}
			function create_onObjectPointerdown1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var onObjectPointerdown1 = actor_move_down_on_click_and_update_light.createNode('onObjectPointerdown');
				onObjectPointerdown1.setName('onObjectPointerdown1');
				const onObjectPointerdown1_nodes: ReturnedNodeDataDict = {};
				onObjectPointerdown1.uiData.setPosition(-100, 0);
				onObjectPointerdown1.params.postCreateSpareParams();
				onObjectPointerdown1.params.runOnSceneLoadHooks();
				return {node: onObjectPointerdown1, children: onObjectPointerdown1_nodes};
			}
			function create_constant1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var constant1 = actor_move_down_on_click_and_update_light.createNode('constant');
				constant1.setName('constant1');
				const constant1_nodes: ReturnedNodeDataDict = {};
				constant1.uiData.setPosition(-100, 250);
				constant1.p.float.set(-0.1);
				constant1.params.postCreateSpareParams();
				constant1.params.runOnSceneLoadHooks();
				return {node: constant1, children: constant1_nodes};
			}
			function create_floatToVec3_1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var floatToVec3_1 = actor_move_down_on_click_and_update_light.createNode('floatToVec3');
				floatToVec3_1.setName('floatToVec3_1');
				const floatToVec3_1_nodes: ReturnedNodeDataDict = {};
				floatToVec3_1.uiData.setPosition(150, 250);
				floatToVec3_1.params.postCreateSpareParams();
				floatToVec3_1.params.runOnSceneLoadHooks();
				return {node: floatToVec3_1, children: floatToVec3_1_nodes};
			}
			function create_setObjectPosition2(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var setObjectPosition2 = actor_move_down_on_click_and_update_light.createNode('setObjectPosition');
				setObjectPosition2.setName('setObjectPosition2');
				const setObjectPosition2_nodes: ReturnedNodeDataDict = {};
				setObjectPosition2.uiData.setPosition(400, 550);
				setObjectPosition2.params.postCreateSpareParams();
				setObjectPosition2.params.runOnSceneLoadHooks();
				return {node: setObjectPosition2, children: setObjectPosition2_nodes};
			}
			function create_constant2(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var constant2 = actor_move_down_on_click_and_update_light.createNode('constant');
				constant2.setName('constant2');
				const constant2_nodes: ReturnedNodeDataDict = {};
				constant2.uiData.setPosition(-100, 400);
				constant2.params.postCreateSpareParams();
				constant2.params.runOnSceneLoadHooks();
				return {node: constant2, children: constant2_nodes};
			}
			function create_floatToVec3_2(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var floatToVec3_2 = actor_move_down_on_click_and_update_light.createNode('floatToVec3');
				floatToVec3_2.setName('floatToVec3_2');
				const floatToVec3_2_nodes: ReturnedNodeDataDict = {};
				floatToVec3_2.uiData.setPosition(150, 400);
				floatToVec3_2.params.postCreateSpareParams();
				floatToVec3_2.params.runOnSceneLoadHooks();
				return {node: floatToVec3_2, children: floatToVec3_2_nodes};
			}
			function create_onPointerup1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var onPointerup1 = actor_move_down_on_click_and_update_light.createNode('onPointerup');
				onPointerup1.setName('onPointerup1');
				const onPointerup1_nodes: ReturnedNodeDataDict = {};
				onPointerup1.uiData.setPosition(-100, 550);
				onPointerup1.params.postCreateSpareParams();
				onPointerup1.params.runOnSceneLoadHooks();
				return {node: onPointerup1, children: onPointerup1_nodes};
			}
			function create_getObject1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var getObject1 = actor_move_down_on_click_and_update_light.createNode('getObject');
				getObject1.setName('getObject1');
				const getObject1_nodes: ReturnedNodeDataDict = {};
				getObject1.uiData.setPosition(650, -250);
				getObject1.p.getCurrentObject.set(false);
				getObject1.p.mask.set('*/spotLightToToggle');
				getObject1.params.postCreateSpareParams();
				getObject1.params.runOnSceneLoadHooks();
				return {node: getObject1, children: getObject1_nodes};
			}
			function create_setObjectAttribute1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var setObjectAttribute1 = actor_move_down_on_click_and_update_light.createNode('setObjectAttribute');
				setObjectAttribute1.setName('setObjectAttribute1');
				const setObjectAttribute1_nodes: ReturnedNodeDataDict = {};
				setObjectAttribute1.uiData.setPosition(1200, 0);
				setObjectAttribute1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setObjectAttribute1.addParam(ParamType.FLOAT, 'lerp', 1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setObjectAttribute1.addParam(ParamType.FLOAT, 'val', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				setObjectAttribute1.params.postCreateSpareParams();
				setObjectAttribute1.params.runOnSceneLoadHooks();
				return {node: setObjectAttribute1, children: setObjectAttribute1_nodes};
			}
			function create_getObjectAttribute1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var getObjectAttribute1 = actor_move_down_on_click_and_update_light.createNode('getObjectAttribute');
				getObjectAttribute1.setName('getObjectAttribute1');
				const getObjectAttribute1_nodes: ReturnedNodeDataDict = {};
				getObjectAttribute1.uiData.setPosition(800, 300);
				getObjectAttribute1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				getObjectAttribute1.params.get('attribName')!.set('getObjectAttribute1');
				getObjectAttribute1.addParam(ParamType.FLOAT, 'defaultFloat', 0, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				getObjectAttribute1.params.postCreateSpareParams();
				getObjectAttribute1.params.runOnSceneLoadHooks();
				return {node: getObjectAttribute1, children: getObjectAttribute1_nodes};
			}
			function create_constant3(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var constant3 = actor_move_down_on_click_and_update_light.createNode('constant');
				constant3.setName('constant3');
				const constant3_nodes: ReturnedNodeDataDict = {};
				constant3.uiData.setPosition(550, 150);
				constant3.p.type.set(4);
				constant3.p.string.set('on');
				constant3.params.postCreateSpareParams();
				constant3.params.runOnSceneLoadHooks();
				return {node: constant3, children: constant3_nodes};
			}
			function create_complement1(actor_move_down_on_click_and_update_light: ActorSopNode) {
				var complement1 = actor_move_down_on_click_and_update_light.createNode('complement');
				complement1.setName('complement1');
				const complement1_nodes: ReturnedNodeDataDict = {};
				complement1.uiData.setPosition(1050, 300);
				complement1.addParam(ParamType.FLOAT, 'in', 0, {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				complement1.params.postCreateSpareParams();
				complement1.params.runOnSceneLoadHooks();
				return {node: complement1, children: complement1_nodes};
			}
			actor_move_down_on_click_and_update_light_nodes['setObjectPosition1'] = create_setObjectPosition1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['onObjectPointerdown1'] = create_onObjectPointerdown1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['constant1'] = create_constant1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['floatToVec3_1'] = create_floatToVec3_1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectPosition2'] = create_setObjectPosition2(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['constant2'] = create_constant2(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['floatToVec3_2'] = create_floatToVec3_2(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['onPointerup1'] = create_onPointerup1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['getObject1'] = create_getObject1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectAttribute1'] = create_setObjectAttribute1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['getObjectAttribute1'] = create_getObjectAttribute1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['constant3'] = create_constant3(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['complement1'] = create_complement1(
				actor_move_down_on_click_and_update_light
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectPosition1'].node.setInput(
				'trigger',
				actor_move_down_on_click_and_update_light_nodes['onObjectPointerdown1'].node,
				'trigger'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectPosition1'].node.setInput(
				'position',
				actor_move_down_on_click_and_update_light_nodes['floatToVec3_1'].node,
				'vec3'
			);
			actor_move_down_on_click_and_update_light_nodes['floatToVec3_1'].node.setInput(
				'y',
				actor_move_down_on_click_and_update_light_nodes['constant1'].node,
				'val'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectPosition2'].node.setInput(
				'trigger',
				actor_move_down_on_click_and_update_light_nodes['onPointerup1'].node,
				'trigger'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectPosition2'].node.setInput(
				'position',
				actor_move_down_on_click_and_update_light_nodes['floatToVec3_2'].node,
				'vec3'
			);
			actor_move_down_on_click_and_update_light_nodes['floatToVec3_2'].node.setInput(
				'y',
				actor_move_down_on_click_and_update_light_nodes['constant2'].node,
				'val'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectAttribute1'].node.setInput(
				'trigger',
				actor_move_down_on_click_and_update_light_nodes['setObjectPosition1'].node,
				'trigger'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectAttribute1'].node.setInput(
				'Object3D',
				actor_move_down_on_click_and_update_light_nodes['getObject1'].node,
				'Object3D'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectAttribute1'].node.setInput(
				'attribName',
				actor_move_down_on_click_and_update_light_nodes['constant3'].node,
				'val'
			);
			actor_move_down_on_click_and_update_light_nodes['setObjectAttribute1'].node.setInput(
				'val',
				actor_move_down_on_click_and_update_light_nodes['complement1'].node,
				'out'
			);
			actor_move_down_on_click_and_update_light_nodes['getObjectAttribute1'].node.setInput(
				'Object3D',
				actor_move_down_on_click_and_update_light_nodes['getObject1'].node,
				'Object3D'
			);
			actor_move_down_on_click_and_update_light_nodes['getObjectAttribute1'].node.setInput(
				'attribName',
				actor_move_down_on_click_and_update_light_nodes['constant3'].node,
				'val'
			);
			actor_move_down_on_click_and_update_light_nodes['complement1'].node.setInput(
				'in',
				actor_move_down_on_click_and_update_light_nodes['getObjectAttribute1'].node,
				'val'
			);
			if (actor_move_down_on_click_and_update_light.childrenController) {
				actor_move_down_on_click_and_update_light.childrenController.selection.set([]);
			}
			actor_move_down_on_click_and_update_light.uiData.setPosition(150, 950);
			actor_move_down_on_click_and_update_light.flags.display.set(true);
			actor_move_down_on_click_and_update_light.params.postCreateSpareParams();
			actor_move_down_on_click_and_update_light.params.runOnSceneLoadHooks();
			return {
				node: actor_move_down_on_click_and_update_light,
				children: actor_move_down_on_click_and_update_light_nodes,
			};
		}
		buttons_nodes['sphere1'] = create_sphere1(buttons);
		buttons_nodes['transform1'] = create_transform1(buttons);
		buttons_nodes['material1'] = create_material1(buttons);
		buttons_nodes['MAT'] = create_MAT(buttons);
		buttons_nodes['roundedBox1'] = create_roundedBox1(buttons);
		buttons_nodes['transform2'] = create_transform2(buttons);
		buttons_nodes['merge1'] = create_merge1(buttons);
		buttons_nodes['actor_on_hover_set_cursor_to_pointer'] = create_actor_on_hover_set_cursor_to_pointer(buttons);
		buttons_nodes['actor_on_hover_update_material'] = create_actor_on_hover_update_material(buttons);
		buttons_nodes['actor_on_hover_update_material1'] = create_actor_on_hover_update_material1(buttons);
		buttons_nodes['material2'] = create_material2(buttons);
		buttons_nodes['text1'] = create_text1(buttons);
		buttons_nodes['merge2'] = create_merge2(buttons);
		buttons_nodes['transform3'] = create_transform3(buttons);
		buttons_nodes['color1'] = create_color1(buttons);
		buttons_nodes['color2'] = create_color2(buttons);
		buttons_nodes['actor_move_down_on_click_and_update_light'] =
			create_actor_move_down_on_click_and_update_light(buttons);
		buttons_nodes['transform1'].node.setInput(0, buttons_nodes['sphere1'].node);
		buttons_nodes['material1'].node.setInput(0, buttons_nodes['transform1'].node);
		buttons_nodes['transform2'].node.setInput(0, buttons_nodes['merge2'].node);
		buttons_nodes['merge1'].node.setInput(0, buttons_nodes['actor_on_hover_update_material'].node);
		buttons_nodes['merge1'].node.setInput(1, buttons_nodes['actor_on_hover_update_material1'].node);
		buttons_nodes['actor_on_hover_set_cursor_to_pointer'].node.setInput(0, buttons_nodes['merge1'].node);
		buttons_nodes['actor_on_hover_update_material'].node.setInput(0, buttons_nodes['material1'].node);
		buttons_nodes['actor_on_hover_update_material1'].node.setInput(0, buttons_nodes['material2'].node);
		buttons_nodes['material2'].node.setInput(0, buttons_nodes['transform2'].node);
		buttons_nodes['merge2'].node.setInput(0, buttons_nodes['color2'].node);
		buttons_nodes['merge2'].node.setInput(1, buttons_nodes['color1'].node);
		buttons_nodes['transform3'].node.setInput(0, buttons_nodes['text1'].node);
		buttons_nodes['color1'].node.setInput(0, buttons_nodes['transform3'].node);
		buttons_nodes['color2'].node.setInput(0, buttons_nodes['roundedBox1'].node);
		buttons_nodes['actor_move_down_on_click_and_update_light'].node.setInput(
			0,
			buttons_nodes['actor_on_hover_set_cursor_to_pointer'].node
		);
		if (buttons.childrenController) {
			buttons.childrenController.selection.set([]);
		}
		buttons.uiData.setPosition(-50, -350);
		buttons.flags.display.set(true);
		buttons.params.postCreateSpareParams();
		buttons.params.runOnSceneLoadHooks();
		return {node: buttons, children: buttons_nodes};
	}
	scene_root_nodes['ground'] = create_ground(root);
	scene_root_nodes['COP'] = create_COP(root);
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);
	scene_root_nodes['buttons'] = create_buttons(root);

	scene.setFrame(0);
	scene.play();
	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/perspectiveCamera1',
		focusedNodes: [], //scene_root_nodes['HTMLElement'].node.children(),
	};
}
// export const sopCSS2DObject_clickable = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/sop/CSS2DObject/clickable`,
// 		createData,
// 		categories: [ExampleCategory.HTML_IN_3D],
// 		relatedNodes: [CSS2DObjectSopNode, CSS2DRendererRopNode],
// 	}),
// 	// new Example({name: `${ExamplePrefix.BY_NODE}/rop/CSS2DRenderer/clickable`, createData}),
// 	// new Example({name: `${ExamplePrefix.BY_CATEGORY}/${ExampleCategory.HTML_IN_3D}/2D Clickable`, createData}),
// ];
